import clsx from "clsx";
import { addDays, isAfter } from "date-fns";
import { useCallback } from "react";

import { WeekDay, calendarWeekDays } from "../../../../types";
import { convertToZonedTime, getDateString } from "../../../../utils";
import { getTranslatedWeekDaysShort } from "../../../../utils/ui-translations";

const getDayClassNames = ({
  isCurrentMonth,
  isSelected,
  isToday,
  disabled,
}: {
  isCurrentMonth?: boolean;
  isSelected?: boolean;
  isToday?: boolean;
  disabled?: boolean;
}) => {
  const baseStyles =
    "h-[3.2rem] flex items-center justify-center text-ui underline-offset-4 decoration-2";
  const selectedStyles = "border rounded border-cyan-540 bg-cyan-240";
  const hoverStyles =
    "hover:border-cyan-240 hover:bg-cyan-140 hover:border hover:rounded";
  const focusStyles =
    "focus:border-cyan-240 focus:bg-cyan-140 focus:border focus:rounded";

  return clsx(
    baseStyles,
    isSelected && selectedStyles,
    (disabled || !isCurrentMonth) && "text-neutral-400",
    isToday && "underline",
    !disabled && isCurrentMonth && "cursor-pointer",
    !disabled && !isSelected && hoverStyles,
    !disabled && !isSelected && focusStyles,
  );
};

type DaysPickerProps = {
  selectedDate: string | undefined | null;
  year: number;
  month: number;
  startDate?: string;
  toDate?: string;
  excludingWeekDays?: WeekDay[];
  excludingDates?: string[];
  disabled?: boolean;
  viewingTimezone?: string | null;
  language?: string;
  onChange: (date: string) => void;
};

export const DaysPicker = ({
  selectedDate,
  year,
  month,
  startDate,
  toDate,
  excludingWeekDays,
  excludingDates,
  disabled,
  viewingTimezone,
  language,
  onChange,
}: DaysPickerProps) => {
  const renderDays = useCallback(() => {
    const todayInViewingTimezone = getDateString(
      convertToZonedTime(new Date(), viewingTimezone),
    );
    const days = [];
    const startOfMonth = new Date(year, month, 1);
    const endOfMonth = new Date(year, month + 1, 0);
    const startOfWeek = (startOfMonth.getDay() + 6) % 7;
    const endOfWeek = (endOfMonth.getDay() + 6) % 7;
    const daysInMonth = endOfMonth.getDate();

    for (let day = 0; day < startOfWeek; day += 1) {
      days.push(
        <div className={getDayClassNames({})} key={`prev-month-${day}`} />,
      );
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const dateString = getDateString(new Date(year, month, day));

      const canSelect =
        isAfter(dateString, addDays(startDate || todayInViewingTimezone, -1)) &&
        (!toDate || isAfter(addDays(toDate, 1), dateString));
      const excluding =
        excludingWeekDays?.includes(new Date(dateString).getDay()) ||
        excludingDates?.some((d) => d === dateString);
      const isDisabled = disabled || !canSelect || excluding;

      days.push(
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div
          className={getDayClassNames({
            isCurrentMonth: true,
            isSelected:
              !!selectedDate && !isDisabled && selectedDate === dateString,
            isToday: dateString === todayInViewingTimezone,
            disabled: isDisabled,
          })}
          key={`current-month-${day}`}
          onClick={() => !isDisabled && onChange(dateString)}
          aria-label={dateString}
          aria-disabled={!!isDisabled}
        >
          {day}
        </div>,
      );
    }

    for (let day = endOfWeek + 1; day < 7; day += 1) {
      days.push(
        <div className={getDayClassNames({})} key={`next-month-${day}`} />,
      );
    }

    return days;
  }, [
    year,
    month,
    selectedDate,
    disabled,
    viewingTimezone,
    startDate,
    toDate,
    excludingWeekDays,
    excludingDates,
    onChange,
  ]);

  const weekDayLabels = getTranslatedWeekDaysShort(language);

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-7 gap-small">
        {calendarWeekDays.map((wd, index) => (
          <div
            className="flex p-[0.8rem] items-center justify-center text-ui font-semibold"
            key={wd}
          >
            {weekDayLabels[index]}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-small px-[0.4rem]">
        {renderDays()}
      </div>
    </div>
  );
};
